import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";

import { fetchTimeOffCapabilities, fetchTimeOffRequests, TimeOffApiError } from "../apis/timeOffRequests";

export const STAFF_DUTY_CHANGED_EVENT = "staff-duty-calendar:changed";
export const timeOffKeys = {
  all: (personId: string) => ["timeOff", personId] as const,
  capabilities: (personId: string) => ["timeOff", personId, "capabilities"] as const,
};
export const retryTimeOff = (count: number, error: Error) => !(error instanceof TimeOffApiError && [401, 403, 404].includes(error.status || 0)) && count < 1;
export function useTimeOffCapabilities(personId: string) {
  return useQuery({
    queryKey: timeOffKeys.capabilities(personId),
    queryFn: ({ signal }) => fetchTimeOffCapabilities(signal),
    enabled: !!personId,
    staleTime: 0,
    refetchOnWindowFocus: true,
    retry: retryTimeOff,
  });
}

export function usePendingTimeOffCount(personId: string) {
  const capabilities = useTimeOffCapabilities(personId);
  const client = useQueryClient();
  const canReview = !capabilities.isError && capabilities.data?.personId === personId && capabilities.data.canReview;
  const query = useQuery({
    queryKey: [...timeOffKeys.all(personId), "pendingCount"],
    queryFn: ({ signal }) => fetchTimeOffRequests({ scope: "review", status: "pending", limit: 1 }, signal),
    enabled: !!canReview,
    staleTime: 0, retry: retryTimeOff, refetchOnWindowFocus: true, refetchInterval: 60000,
  });
  useEffect(() => {
    if (!personId) return;
    const refresh = () => { void client.invalidateQueries({ queryKey: timeOffKeys.all(personId) }); };
    window.addEventListener(STAFF_DUTY_CHANGED_EVENT, refresh);
    return () => window.removeEventListener(STAFF_DUTY_CHANGED_EVENT, refresh);
  }, [client, personId]);
  return canReview && !query.isError ? query.data?.pendingCount || 0 : 0;
}
